import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { CustomField, CustomFieldEntry } from '../types/customFields';
import env from '../config/env';
import { useEncryption } from './EncryptionContext';

interface CustomFieldsContextType {
  customFields: CustomField[];
  isLoading: boolean;
  getFieldEntriesForJournal: (journalEntryId: string) => Promise<CustomFieldEntry[]>;
  createCustomField: (field: Omit<CustomField, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateCustomField: (fieldId: string, updates: Partial<CustomField>) => Promise<void>;
  deleteCustomField: (fieldId: string) => Promise<void>;
  archiveCustomField: (fieldId: string) => Promise<void>;
  toggleShowInNewEntries: (fieldId: string) => Promise<void>;
  fetchCustomFields: () => Promise<void>;
}

const CustomFieldsContext = createContext<CustomFieldsContextType | undefined>(undefined);

export const useCustomFields = () => {
  const context = useContext(CustomFieldsContext);
  if (!context) {
    throw new Error('useCustomFields must be used within a CustomFieldsProvider');
  }
  return context;
};

export const CustomFieldsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { encrypt, decrypt } = useEncryption();

  useEffect(() => {
    loadCustomFields();
  }, []);

  const loadCustomFields = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${env.API_URL}/custom-fields`);
      // Decrypt field names after fetching
      const decryptedFields = await Promise.all(data.map(async (field: CustomField) => ({
        ...field,
        name: await decrypt(field.encrypted_name)
      })));
      setCustomFields(decryptedFields);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldEntriesForJournal = async (journalEntryId: string): Promise<CustomFieldEntry[]> => {
    try {
      const { data } = await axios.get(`${env.API_URL}/custom-field-entries/journal/${journalEntryId}`);
      // Decrypt field values and names
      const decryptedEntries = await Promise.all(data.map(async (entry: CustomFieldEntry) => ({
        ...entry,
        value: await decrypt(entry.encrypted_value),
        field_name: await decrypt(entry.encrypted_name)
      })));
      return decryptedEntries;
    } catch (error) {
      console.error('Error loading field entries:', error);
      return [];
    }
  };

  const createCustomField = async (field: Omit<CustomField, 'id' | 'created_at' | 'updated_at'>): Promise<void> => {
    try {
      // Encrypt the field name before sending to backend
      const encrypted_name = await encrypt(field.name);
      await axios.post(`${env.API_URL}/custom-fields`, {
        ...field,
        encrypted_name,
      });
      await loadCustomFields();
    } catch (error) {
      console.error('Error creating custom field:', error);
      throw error;
    }
  };

  const updateCustomField = async (fieldId: string, updates: Partial<CustomField>): Promise<void> => {
    try {
      const updatesWithEncryption = { ...updates };
      if (updates.name) {
        updatesWithEncryption.encrypted_name = await encrypt(updates.name);
        delete updatesWithEncryption.name;
      }
      await axios.patch(`${env.API_URL}/custom-fields/${fieldId}`, updatesWithEncryption);
      await loadCustomFields();
    } catch (error) {
      console.error('Error updating custom field:', error);
      throw error;
    }
  };

  const deleteCustomField = async (fieldId: string): Promise<void> => {
    try {
      await axios.delete(`${env.API_URL}/custom-fields/${fieldId}`);
      await loadCustomFields();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      throw error;
    }
  };

  const archiveCustomField = async (fieldId: string): Promise<void> => {
    await updateCustomField(fieldId, { is_archived: true });
  };

  const toggleShowInNewEntries = async (fieldId: string): Promise<void> => {
    const field = customFields.find(f => f.id === fieldId);
    if (field) {
      await updateCustomField(fieldId, { show_in_new_entries: !field.show_in_new_entries });
    }
  };

  const value = {
    customFields,
    isLoading,
    getFieldEntriesForJournal,
    createCustomField,
    updateCustomField,
    deleteCustomField,
    archiveCustomField,
    toggleShowInNewEntries,
    fetchCustomFields: loadCustomFields,
  };

  return (
    <CustomFieldsContext.Provider value={value}>
      {children}
    </CustomFieldsContext.Provider>
  );
};

export default CustomFieldsProvider; 